import React, { lazy, Suspense } from "react";
import { Route, Navigate, useLocation } from "react-router-dom";
import { useAtomValue } from "jotai";
import { isAuthenticatedAtom } from "@/stores/auth.atom";
import { FeedPage } from "@/pages/feed";
import { PostDetailPage } from "@/pages/post-detail";
import PlaceDetailPage from "@/pages/places/[id]";
import CreatePostPage from "@/pages/create-post";
import EditPostPage from "@/pages/edit-post";

// Direct imports for marketplace and other pages
import MarketplacePage from "@/pages/marketplace/index";
import ProductDetailPage from "@/pages/marketplace/product-detail";
import CartPage from "@/pages/marketplace/cart-page";
import GroupsPage from "@/pages/groups/index";
import EventsPage from "@/pages/events/index";
import EventDetailPage from "@/pages/events/[id]";
import CreateEventPage from "@/pages/events/create";
import FavoritePlacesPage from "@/pages/favorite-places/index";
import SavedPage from "@/pages/saved/index";
import SavedProductPage from "@/pages/saved-product/index";
import MyGroupPage from "@/pages/my-group/index";
import MyGroupDetailPage from "@/pages/my-group/[id]/index";
import GroupDetailPage from "@/pages/group-detail/index";

// Lazy load pages for better performance
const CommunitiesListPage = lazy(() => import("@/pages/communities/index"));
const CommunityDetailPage = lazy(() => import("@/pages/communities/detail"));
const CommunityEditPage = lazy(() => import("@/pages/communities/edit"));
const ChatPage = lazy(() => import("@/pages/chat/index"));
const NotificationsListPage = lazy(() => import("@/pages/notifications/index"));
const AllNotificationsPage = lazy(() => import("@/pages/notifications/all"));
const NotificationPreferencesPage = lazy(
  () => import("@/pages/notifications/preferences")
);
const UserProfilePage = lazy(() => import("@/pages/users/profile"));
const SettingsPage = lazy(() => import("@/pages/settings/index"));
const EditProfilePage = lazy(() => import("@/pages/profile/edit/index"));
const EmergencyContactPage = lazy(
  () => import("@/pages/profile/edit/emergencycontact")
);
const ProfilePage = lazy(() => import("@/pages/profile/index"));
const AccountSettingsPage = lazy(() => import("@/pages/settings/account"));
const NotificationsSettingsPage = lazy(
  () => import("@/pages/settings/notifications")
);
const PrivacySettingsPage = lazy(() => import("@/pages/settings/privacy"));
const LocalStoragePage = lazy(() => import("@/pages/settings/local-storage"));
const DeleteAccountPage = lazy(() => import("@/pages/settings/delete-account"));

// Loading fallback component
const LoadingFallback = () => <div className="p-4">Loading...</div>;

// Protected Route Wrapper
export const ProtectedRoute = ({ children }: { children: React.ReactNode }) => {
  const isAuthenticated = useAtomValue(isAuthenticatedAtom);
  const location = useLocation();

  if (!isAuthenticated) {
    return <Navigate to="/login" state={{ from: location }} replace />;
  }

  return children;
};

export const protectedRoutes = [
  // Main Feed Route
  <Route
    key="feed"
    path="/feed"
    element={
      <ProtectedRoute>
        <FeedPage />
      </ProtectedRoute>
    }
  />,

  // Post Routes
  <Route
    key="create-post"
    path="/create-post"
    element={
      <ProtectedRoute>
        <CreatePostPage />
      </ProtectedRoute>
    }
  />,
  <Route
    key="post-detail"
    path="/posts/:id"
    element={
      <ProtectedRoute>
        <PostDetailPage />
      </ProtectedRoute>
    }
  />,
  <Route
    key="edit-post"
    path="/edit-post/:id"
    element={
      <ProtectedRoute>
        <EditPostPage />
      </ProtectedRoute>
    }
  />,

  // Profile Routes
  <Route
    key="profile"
    path="/profile"
    element={
      <ProtectedRoute>
        <ProfilePage />
      </ProtectedRoute>
    }
  />,
  <Route
    key="profile-edit"
    path="/profile/edit"
    element={
      <ProtectedRoute>
        <EditProfilePage />
      </ProtectedRoute>
    }
  />,
  <Route
    key="profile-emergency"
    path="/profile/edit/emergencycontact"
    element={
      <ProtectedRoute>
        <EmergencyContactPage />
      </ProtectedRoute>
    }
  />,
  <Route
    key="user-profile"
    path="/users/:id"
    element={
      <ProtectedRoute>
        <UserProfilePage />
      </ProtectedRoute>
    }
  />,

  // Community Routes
  <Route
    key="communities"
    path="/communities"
    element={
      <ProtectedRoute>
        <CommunitiesListPage />
      </ProtectedRoute>
    }
  />,
  <Route
    key="communities-create"
    path="/communities/create"
    element={
      <ProtectedRoute>
        <CommunityEditPage />
      </ProtectedRoute>
    }
  />,
  <Route
    key="community-detail"
    path="/communities/:id"
    element={
      <ProtectedRoute>
        <CommunityDetailPage />
      </ProtectedRoute>
    }
  />,
  <Route
    key="community-edit"
    path="/communities/:id/edit"
    element={
      <ProtectedRoute>
        <CommunityEditPage />
      </ProtectedRoute>
    }
  />,

  // Chat/Messages Routes
  <Route
    key="chat"
    path="/chat"
    element={
      <ProtectedRoute>
        <ChatPage />
      </ProtectedRoute>
    }
  />,
  <Route
    key="chat-detail"
    path="/chat/:chatId"
    element={
      <ProtectedRoute>
        <ChatPage />
      </ProtectedRoute>
    }
  />,

  // Notifications Routes
  <Route
    key="notifications"
    path="/notifications"
    element={
      <ProtectedRoute>
        <NotificationsListPage />
      </ProtectedRoute>
    }
  />,
  <Route
    key="notifications-all"
    path="/notifications/all"
    element={
      <ProtectedRoute>
        <AllNotificationsPage />
      </ProtectedRoute>
    }
  />,
  <Route
    key="notifications-preferences"
    path="/notifications/preferences"
    element={
      <ProtectedRoute>
        <NotificationPreferencesPage />
      </ProtectedRoute>
    }
  />,

  // Settings Routes
  <Route
    key="settings"
    path="/settings"
    element={
      <ProtectedRoute>
        <SettingsPage />
      </ProtectedRoute>
    }
  />,
  <Route
    key="settings-account"
    path="/settings/account"
    element={
      <ProtectedRoute>
        <AccountSettingsPage />
      </ProtectedRoute>
    }
  />,
  <Route
    key="settings-notifications"
    path="/settings/notifications"
    element={
      <ProtectedRoute>
        <NotificationsSettingsPage />
      </ProtectedRoute>
    }
  />,
  <Route
    key="settings-privacy"
    path="/settings/privacy"
    element={
      <ProtectedRoute>
        <PrivacySettingsPage />
      </ProtectedRoute>
    }
  />,
  <Route
    key="settings-local-storage"
    path="/settings/local-storage"
    element={
      <ProtectedRoute>
        <LocalStoragePage />
      </ProtectedRoute>
    }
  />,
  <Route
    key="settings-delete-account"
    path="/settings/delete-account"
    element={<DeleteAccountPage />}
  />,

  // Places Route
  <Route
    key="places"
    path="/places/:id"
    element={
      <ProtectedRoute>
        <PlaceDetailPage />
      </ProtectedRoute>
    }
  />,

  // Marketplace Routes
  <Route
    key="marketplace"
    path="/marketplace"
    element={
      <ProtectedRoute>
        <MarketplacePage />
      </ProtectedRoute>
    }
  />,
  <Route
    key="marketplace-product"
    path="/marketplace/product/:id"
    element={
      <ProtectedRoute>
        <ProductDetailPage />
      </ProtectedRoute>
    }
  />,
  <Route
    key="marketplace-cart"
    path="/marketplace/cart"
    element={
      <ProtectedRoute>
        <CartPage />
      </ProtectedRoute>
    }
  />,

  // Favorite Places Route
  <Route
    key="favorite-places"
    path="/favorite-places"
    element={
      <ProtectedRoute>
        <FavoritePlacesPage />
      </ProtectedRoute>
    }
  />,

  // Groups Routes
  <Route
    key="groups"
    path="/groups"
    element={
      <ProtectedRoute>
        <GroupsPage />
      </ProtectedRoute>
    }
  />,
  <Route
    key="group-detail"
    path="/group-detail"
    element={
      <ProtectedRoute>
        <GroupDetailPage />
      </ProtectedRoute>
    }
  />,

  // Saved Routes
  <Route
    key="saved"
    path="/saved"
    element={
      <ProtectedRoute>
        <SavedPage />
      </ProtectedRoute>
    }
  />,
  <Route
    key="saved-product"
    path="/saved_product"
    element={
      <ProtectedRoute>
        <SavedProductPage />
      </ProtectedRoute>
    }
  />,

  // My Group Routes
  <Route
    key="my-group"
    path="/my_group"
    element={
      <ProtectedRoute>
        <MyGroupPage />
      </ProtectedRoute>
    }
  />,
  <Route
    key="my-group-detail"
    path="/my_group/:id"
    element={
      <ProtectedRoute>
        <MyGroupDetailPage />
      </ProtectedRoute>
    }
  />,

  // Events Routes
  <Route
    key="events"
    path="/events"
    element={
      <ProtectedRoute>
        <EventsPage />
      </ProtectedRoute>
    }
  />,
  <Route
    key="event-detail"
    path="/events/:id"
    element={
      <ProtectedRoute>
        <Suspense fallback={<LoadingFallback />}>
          <EventDetailPage />
        </Suspense>
      </ProtectedRoute>
    }
  />,
  <Route
    key="event-create"
    path="/events/create"
    element={
      <ProtectedRoute>
        <Suspense fallback={<LoadingFallback />}>
          <CreateEventPage />
        </Suspense>
      </ProtectedRoute>
    }
  />,

  // Test Route
  <Route key="test" path="/test" element={<div className="p-4">Test Route</div>} />,
];
